<template>
  <div>
    <b-overlay :show="showoverlay" rounded="sm" class="col-md-12">

      <div class="clearfix row py-2 mb-2 no-margin">
       <div class="col-md-5 pr-0 cursor-pointer text-left pl-0">
          <b-input-group class="from_date-range-picker d-flex flex-row align-items-end justify-content-start">
            <b-form-group id="jc-from_date-group" label-for="jc_from_date" class="mb-0 mr-3" label="From Date">
              <b-input-group class="mb-0">
                <b-form-input id="jc_from_date" name="from_date" v-model="from_date" type="text" placeholder="YYYY-MM-DD" autocomplete="off"
                  v-validate="'required|date_format:yyyy-MM-dd'" :state="validateState('from_date')" data-vv-as="From Date"/>
                <b-input-group-append class="mb-0">
                  <b-form-datepicker @input="reset" class="mb-0" id="jc_from_picker" button-only right locale="en-US" v-model="from_date"/>
                </b-input-group-append>
              </b-input-group>
              <b-form-invalid-feedback class="d-block">{{ veeErrors.first('from_date') }}</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group id="jc-to_date-group" label-for="jc_to_date" class="mb-0" label="To Date">
              <b-input-group class="mb-0">
                <b-form-input id="jc_to_date" name="to_date" v-model="to_date" type="text" placeholder="YYYY-MM-DD" autocomplete="off"
                  v-validate="'required|date_format:yyyy-MM-dd'" :state="validateState('to_date')" data-vv-as="To Date"/>
                <b-input-group-append class="mb-0">
                  <b-form-datepicker @input="reset" class="mb-0" id="jc_to_picker" button-only right locale="en-US" v-model="to_date"/>
                </b-input-group-append>
              </b-input-group>
              <b-form-invalid-feedback class="d-block">{{ veeErrors.first('to_date') }}</b-form-invalid-feedback>
            </b-form-group>
          </b-input-group>
        </div>

        <div class="col-md-7 no-margin p-0">
          <div class="row d-flex justify-content-end no-margin">
            <b-button variant="secondary" class="m-1" @click="reset()">Reset</b-button>
            <b-button variant="secondary" class="m-1" @click="previewOrDownload(0)">Preview Job Card</b-button>
            <b-dropdown variant="secondary" class="m-1" right>
              <template #button-content>Download Job Card</template>
              <b-dropdown-item @click="previewOrDownload(2)">Download as PDF</b-dropdown-item>
              <b-dropdown-item @click="previewOrDownload(1)">Download as Excel</b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>

      <!-- Preview: per-employee job cards -->
      <div v-if="preview" class="job-card-wrap">
        <div v-for="emp in preview_data" :key="emp.employee_id" class="jc-card mb-5">

          <!-- Header -->
          <div class="jc-header text-center pb-2 mb-2">
            <div v-if="emp.logo" class="text-center mb-1">
              <img :src="$api_url + '/images/company/' + emp.logo" style="width: 80px;" />
            </div>
            <div class="jc-company-name">{{ emp.company_name }}</div>
            <div v-if="emp.company_address" class="jc-address">{{ emp.company_address }}</div>
            <div class="jc-title font-weight-bold">JOB CARD</div>
            <div class="jc-period">Period: {{ formatDate(from_date) }} to {{ formatDate(to_date) }}</div>
          </div>

          <!-- Employee info table -->
          <table class="table table-bordered table-sm jc-info-table mb-2">
            <tbody>
              <tr>
                <td class="font-weight-bold">Name</td>
                <td>: {{ emp.name }}</td>
                <td class="font-weight-bold">Department</td>
                <td>: {{ emp.department }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">ID</td>
                <td>: {{ emp.employee_code || emp.employee_id }}</td>
                <td class="font-weight-bold">Branch</td>
                <td>: {{ emp.branch }}</td>
              </tr>
              <tr>
                <td class="font-weight-bold">Designation</td>
                <td>: {{ emp.designation }}</td>
                <td class="font-weight-bold">Joining Date</td>
                <td>: {{ emp.joining_date }}</td>
              </tr>
            </tbody>
          </table>

          <!-- Totals strip -->
          <div class="monthly-report-wrap">
            <table class="table table-bordered table-sm text-center mb-0">
              <tr>
                <td class="bg-light">Present: <strong>{{ emp.totals.present }}</strong></td>
                <td class="bg-light">Absent: <strong>{{ emp.totals.absent }}</strong></td>
                <td class="bg-light">Off Day: <strong>{{ emp.totals.offday }}</strong></td>
                <td class="bg-light">Holiday: <strong>{{ emp.totals.holiday }}</strong></td>
                <td class="bg-light">Mobility: <strong>{{ emp.totals.mobility }}</strong></td>
                <td class="bg-light">Leave: <strong>{{ emp.totals.leave }}</strong></td>
                <td class="bg-light">Late: <strong>{{ emp.totals.late }}</strong></td>
                <td class="bg-light">Early: <strong>{{ emp.totals.early }}</strong></td>
              </tr>
              <tr v-if="emp.ot_policy != null">
                <td colspan="2" class="bg-light">Total Late Hour(s): <strong>{{ emp.totals.total_late }}</strong></td>
                <td colspan="2" class="bg-light">Total Early Hour(s): <strong>{{ emp.totals.total_early }}</strong></td>
                <td colspan="2" class="bg-light">Total OT Hour(s): <strong>{{ emp.totals.overtime }}</strong></td>
                <td colspan="2" class="bg-light">Total Duty Hour(s): <strong>{{ emp.totals.total_duty }}</strong></td>
              </tr>
              <tr v-else>
                <td colspan="2" class="bg-light">Total Late Hour(s): <strong>{{ emp.totals.total_late }}</strong></td>
                <td colspan="3" class="bg-light">Total Early Hour(s): <strong>{{ emp.totals.total_early }}</strong></td>
                <td colspan="3" class="bg-light">Total Duty Hour(s): <strong>{{ emp.totals.total_duty }}</strong></td>
              </tr>
            </table>
          </div>

          <!-- Daily attendance table -->
          <div class="monthly-report-wrap mt-3">
            <table class="table roster-la-report-table border">
              <thead class="table-head">
                <tr>
                  <th>Date</th>
                  <th>Shift</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Late</th>
                  <th>Early</th>
                  <th>Working Hour(s)</th>
                  <th v-if="emp.ot_policy != null">OT</th>
                  <th>Status</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="day in emp.daily" :key="day.date" :style="rowStyle(day.status, day.is_mobility)">
                  <td>{{ formatDateDMY(day.date) }}</td>
                  <td class="text-left">{{ day.shift_name }}</td>
                  <td>{{ day.in_time }}</td>
                  <td>{{ day.out_time }}</td>
                  <td>{{ day.late_min }}</td>
                  <td>{{ day.early_min }}</td>
                  <td>{{ day.working_hours }}</td>
                  <td v-if="emp.ot_policy != null">{{ day.overtime }}</td>
                  <td>{{ day.status }}</td>
                  <td class="text-left">{{ day.remarks != '-' ? day.remarks : '-' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

        </div>
      </div>

      <!-- Employee list (shown before preview) -->
      <div v-else class="row" id="jc-assignment">
        <div class="col-12">
          <b-badge class="d-inline mb-3" variant="info">Selected Employee: {{ employee_ids.length }}</b-badge>
          <div class="card border-0 no-shadow">
            <common-list
              :checkbox="1"
              :is_action="0"
              :appended_column="appended_column"
              :key="list_key"
              :company_options="company_options"
              :employees="custom_list"
              :all_employee="'no'"
              :default_column="default_column"
              :primary_key="'employee_id'"
              v-on:childToParent="listReturn">
            </common-list>
          </div>
        </div>
      </div>

    </b-overlay>
  </div>
</template>

<script>
export default {
  data() {
    const now = new Date();
    let firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    let lastDay  = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    let format = function(d) {
      let year  = d.getFullYear();
      let month = ('0' + (d.getMonth() + 1)).slice(-2);
      let day   = ('0' + d.getDate()).slice(-2);
      return year + '-' + month + '-' + day;
    };
    return {
      from_date:      format(firstDay),
      to_date:        format(lastDay),
      list_key:       0,
      custom_list:    [],
      default_column: ['name', 'phone', 'email', 'jobposition', 'department', 'branch', 'leave_policy', 'leave_type'],
      appended_column:[],
      company_options:[],
      employee_ids:   [],
      preview_data:   [],
      showoverlay:    false,
      preview:        false,
    };
  },
  methods: {
    reset() {
      this.preview = false;
      this.employee_ids = [];
      this.list_key++;
      this.getEmployees();
    },
    getEmployees() {
      axios.get(this.$api_url + '/leave-attendance/employees/list', { params: { all: this.all, fields: 'offday,roster,shift', from: this.from_date, to: this.to_date } })
        .then(response => {
          this.showoverlay = false;
          if (response.data.error == 1) {
            this.notice = response.data.notice;
          } else {
            this.custom_list    = response.data.employee_data;
            this.company_options = response.data.options;
            this.list_key++;
          }
        }).catch(e => {
          this.serverError(e);
        });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return String(d.getDate()).padStart(2, '0') + ' ' + months[d.getMonth()] + ' ' + d.getFullYear();
    },
    formatDateDMY(dateStr) {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      return parts[2] + '-' + parts[1] + '-' + parts[0];
    },
    rowStyle(status, isMobility) {
      const map = {
        'Holiday':          { background: '#e0e0e0', color: '#555' },
        'Off Day':          { background: '#e0e0e0', color: '#555' },
        'Absent':           { background: '#fde8e8', color: '#c0392b' },
        'Present':          { background: '#eafaf1', color: '#1e8449' },
        'Late':             { background: '#fff9e6', color: '#b7950b' },
        'Early Out':        { background: '#fff9e6', color: '#b7950b' },
        'Late & Early Out': { background: '#fff9e6', color: '#b7950b' },
        'On Leave':         { background: '#f5eef8', color: '#7d3c98' },
        'Leave Pending':    { background: '#f5eef8', color: '#7d3c98' },
        'Compensatory Off': { background: '#eaf0fb', color: '#2e4fa3' },
      };
      if (isMobility) return { background: '#dbeafe', color: '#1d4ed8' };
      return map[status] || {};
    },
    listReturn(data) {
      this.employee_ids = data.selected;
    },
    previewOrDownload(flg) {
      this.$validator.validateAll().then(valid => {
      if (!valid) return;
      if (!this.checkNet()) return false;
      if (this.employee_ids.length === 0) {
        this.$bvToast.toast(
          flg === 0 ? 'Please select employee(s) to generate job card' : 'Please select employee(s) to download job card',
          { title: 'Failed', variant: 'danger', solid: true }
        );
        return;
      }
      if (flg > 0) {
        this.$bvToast.toast('We are preparing the download, this may take a while.', {
          title: 'Hold Tight', variant: 'info', solid: true
        });
      }

      let config = {};
      if (flg > 0) {
        config = { responseType: 'blob' };
      }

      this.showoverlay = true;
      let formData = new FormData();
      formData.append('employee_ids', JSON.stringify(this.employee_ids));
      formData.append('from_date', this.from_date);
      formData.append('to_date', this.to_date);
      formData.append('export', flg);

      axios.post(this.$api_url + '/leave-attendance/job-card', formData, config)
        .then(response => {
          this.showoverlay = false;
          if (flg === 0) {
            this.preview_data = response.data;
            this.preview = true;
          } else {
            this.global_download_file_content(response);
          }
        }).catch(error => {
          this.showoverlay = false;
          this.serverError(error);
        });
      });
    },
  },
  created() {
    this.getEmployees();
  }
};
</script>
